// src/pages/admin/AdminRolesPermissions.js
// ============================================================================
// RÔLES & PERMISSIONS — Maxula
// Livré par AbodyStudio Limited — https://abodystudio.com
//
// Trois onglets :
//   1. Rôles       — créer, renommer, supprimer
//   2. Permissions — LE TABLEAU : une ligne par action, une colonne par rôle,
//                    une case à l'intersection
//   3. Assignation — quel compte porte quel rôle
//
// Le tableau est le cœur : cocher une case modifie LE RÔLE, donc tous les
// comptes qui le portent, immédiatement. C'est ce qui évite de rouvrir chaque
// compte un par un.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiShield, FiPlus, FiTrash2, FiEdit3, FiLoader, FiSearch,
  FiCheck, FiX, FiSave, FiUsers, FiLock, FiBriefcase
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';

const TABS = [
  { k: 'roles', l: 'Rôles' },
  { k: 'matrix', l: 'Permissions' },
  { k: 'assign', l: 'Assignation des rôles' },
];

const AdminRolesPermissions = () => {
  const [tab, setTab] = useState('matrix');
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // `${roleId}:${permission}` en cours

  const [staff, setStaff] = useState([]);
  const [staffQuery, setStaffQuery] = useState('');

  const [newRole, setNewRole] = useState({ label: '', description: '' });
  const [editing, setEditing] = useState(null);

  const loadMatrix = useCallback(async () => {
    try {
      const res = await API.get('/admin/staff/matrix');
      const d = res?.data?.data || {};
      setGroups(d.groups || []);
      setRoles(d.roles || []);
    } catch (e) {
      // Le message du serveur nomme desormais la cause reelle (colonne ou
      // table manquante, fichier obsolete). L'afficher tel quel evite le
      // « Erreur serveur » qui envoie chercher au mauvais endroit.
      const body = e?.response?.data || {};
      const msg = body.message || 'Chargement impossible';
      toast.error(body.hint ? `${msg} — ${body.hint}` : msg, { autoClose: 10000 });
    }
  }, []);

  const loadStaff = useCallback(async () => {
    try {
      const res = await API.get('/admin/staff');
      setStaff(res?.data?.data || []);
    } catch (e) { /* onglet secondaire, on n'alarme pas */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadMatrix(), loadStaff()]);
      setLoading(false);
    })();
  }, [loadMatrix, loadStaff]);

  // ---- cocher / décocher une case ----
  // L'état local bascule AVANT la réponse du serveur : sur un tableau de cette
  // taille, attendre l'aller-retour à chaque clic rendrait la saisie pénible.
  // En cas d'échec on remet la case dans son état d'origine.
  const togglePerm = async (role, permission) => {
    if (role.isSuper) {
      toast.info('Le Super Admin a tous les droits par définition.');
      return;
    }
    const key = `${role.id}:${permission}`;
    if (busy === key) return;

    const granted = !role.permissions.includes(permission);
    setBusy(key);
    setRoles((prev) => prev.map((r) => (r.id !== role.id ? r : {
      ...r,
      permissions: granted
        ? [...r.permissions, permission]
        : r.permissions.filter((p) => p !== permission),
    })));

    try {
      await API.put(`/admin/staff/roles/${role.id}/permissions`, { permission, granted });
    } catch (e) {
      setRoles((prev) => prev.map((r) => (r.id !== role.id ? r : {
        ...r,
        permissions: granted
          ? r.permissions.filter((p) => p !== permission)
          : [...r.permissions, permission],
      })));
      toast.error(e?.response?.data?.message || 'Modification refusée');
    } finally {
      setBusy(null);
    }
  };

  const createRole = async () => {
    if (newRole.label.trim().length < 2) { toast.error('Nom du rôle requis'); return; }
    try {
      await API.post('/admin/staff/roles', newRole);
      setNewRole({ label: '', description: '' });
      await loadMatrix();
      toast.success('Rôle créé — cochez ses permissions dans l\'onglet Permissions');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Échec');
    }
  };

  const saveRole = async () => {
    if (!editing) return;
    try {
      await API.put(`/admin/staff/roles/${editing.id}`, {
        label: editing.label,
        description: editing.description,
      });
      setEditing(null);
      await loadMatrix();
      toast.success('Rôle mis à jour');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Échec');
    }
  };

  const removeRole = async (role) => {
    if (!window.confirm(`Supprimer le rôle « ${role.label} » ?`)) return;
    try {
      await API.delete(`/admin/staff/roles/${role.id}`);
      await loadMatrix();
      toast.success('Rôle supprimé');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Suppression refusée');
    }
  };

  const assignRole = async (userId, staffRoleId) => {
    try {
      await API.put(`/admin/staff/${userId}/role`, { staffRoleId: staffRoleId || null });
      await Promise.all([loadStaff(), loadMatrix()]);
      toast.success('Rôle assigné');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Échec');
    }
  };

  const visibleStaff = useMemo(() => {
    const q = staffQuery.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter((u) =>
      `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q));
  }, [staff, staffQuery]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
        <FiLoader className="animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-1">
        <FiShield className="text-[#e11d7b]" size={22} />
        <h1 className="text-2xl font-bold">Rôles &amp; Permissions</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Gérez les rôles et les permissions des utilisateurs de l'administration.
      </p>

      <div className="flex items-center gap-1 border-b mb-5">
        {TABS.map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              tab === t.k
                ? 'border-[#e11d7b] text-[#e11d7b]'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* ================== ONGLET PERMISSIONS (le tableau) ================== */}
      {tab === 'matrix' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* overflow-x-auto et non overflow-hidden : avec 8 rôles ou plus, les
              dernières colonnes sortent de l'écran et doivent rester
              atteignables au défilement. */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 font-bold text-gray-700 sticky left-0 bg-gray-50 z-10 min-w-[280px]">
                    PERMISSIONS
                    <div className="text-[11px] font-normal text-gray-400">Module / Action</div>
                  </th>
                  {roles.map((r) => (
                    <th key={r.id} className="px-3 py-3 text-center min-w-[110px]">
                      <div className="font-bold text-gray-800 flex items-center justify-center gap-1">
                        {r.isSuper ? <FiLock size={11} className="text-amber-500" /> : null}
                        {r.label}
                      </div>
                      <div className="text-[11px] font-normal text-[#e11d7b] truncate">
                        {r.description || '—'}
                      </div>
                      <div className="text-[10px] font-normal text-gray-400">
                        {r.memberCount} compte{r.memberCount > 1 ? 's' : ''}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.key}>
                    <tr className="bg-gray-50/60">
                      <td
                        className="px-4 py-2 font-bold text-[#e11d7b] sticky left-0 bg-gray-50/60"
                        colSpan={1}
                      >
                        {g.label}
                      </td>
                      <td colSpan={roles.length} />
                    </tr>
                    {g.items.map((item) => (
                      <tr key={item.key} className="border-b last:border-0 hover:bg-gray-50/50">
                        <td className="px-4 py-1.5 sticky left-0 bg-white">
                          <span className={item.danger ? 'text-red-700' : 'text-gray-700'}>
                            {item.label}
                          </span>
                        </td>
                        {roles.map((r) => {
                          const on = r.isSuper || r.permissions.includes(item.key);
                          const key = `${r.id}:${item.key}`;
                          return (
                            <td key={r.id} className="px-3 py-1.5 text-center">
                              <button
                                type="button"
                                onClick={() => togglePerm(r, item.key)}
                                disabled={busy === key}
                                title={r.isSuper ? 'Super Admin : accès total' : (on ? 'Retirer' : 'Accorder')}
                                className={`w-5 h-5 rounded border inline-flex items-center justify-center transition ${
                                  r.isSuper
                                    ? 'bg-gray-300 border-gray-300 cursor-not-allowed'
                                    : on
                                      ? 'bg-[#e11d7b] border-[#e11d7b] text-white hover:opacity-80'
                                      : 'bg-white border-gray-300 hover:border-[#e11d7b]'
                                } ${busy === key ? 'opacity-50' : ''}`}
                              >
                                {on ? <FiCheck size={13} /> : null}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t text-xs text-gray-500">
            Chaque case s'enregistre au clic. La modification s'applique
            <strong> immédiatement à tous les comptes</strong> qui portent ce rôle.
            En rouge : les droits sensibles.
          </div>
        </div>
      )}

      {/* ================== ONGLET RÔLES ================== */}
      {tab === 'roles' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow divide-y">
            {roles.map((r) => (
              <div key={r.id} className="p-4">
                {editing?.id === r.id ? (
                  <div className="space-y-2">
                    <input
                      value={editing.label}
                      onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm font-semibold"
                    />
                    <input
                      value={editing.description || ''}
                      onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                      placeholder="Description"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                    <div className="flex gap-2">
                      <button onClick={saveRole} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#e11d7b] text-white text-xs font-bold">
                        <FiSave size={13} /> Enregistrer
                      </button>
                      <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold">
                        <FiX size={13} /> Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold text-gray-800 flex items-center gap-2">
                        {r.isSuper ? <FiLock size={13} className="text-amber-500" /> : null}
                        {r.label}
                        {r.isSystem ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-semibold">
                            système
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500">{r.description || '—'}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {r.isSuper ? 'Tous les droits' : `${r.permissions.length} permission${r.permissions.length > 1 ? 's' : ''}`}
                        {' · '}
                        {r.memberCount} compte{r.memberCount > 1 ? 's' : ''}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => setEditing({ id: r.id, label: r.label, description: r.description })}
                        className="p-2 rounded-lg border hover:bg-gray-50"
                        title="Renommer"
                      >
                        <FiEdit3 size={14} />
                      </button>
                      {!r.isSystem && (
                        <button
                          onClick={() => removeRole(r)}
                          className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          title="Supprimer"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow p-4 h-fit">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2">
              <FiPlus size={15} /> Ajouter un rôle
            </div>
            <input
              value={newRole.label}
              onChange={(e) => setNewRole({ ...newRole, label: e.target.value })}
              placeholder="Nom (ex. Commercial Nord)"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
            />
            <input
              value={newRole.description}
              onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
              placeholder="Description courte"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
            />
            <button
              onClick={createRole}
              className="w-full px-4 py-2 rounded-lg bg-[#e11d7b] text-white text-sm font-semibold hover:opacity-90"
            >
              Créer le rôle
            </button>
            <p className="text-xs text-gray-400 mt-2">
              Le rôle est créé <strong>sans aucune permission</strong>. Cochez-les
              ensuite dans l'onglet Permissions.
            </p>
          </div>
        </div>
      )}

      {/* ================== ONGLET ASSIGNATION ================== */}
      {tab === 'assign' && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="relative mb-3">
            <FiSearch size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={staffQuery}
              onChange={(e) => setStaffQuery(e.target.value)}
              placeholder="Rechercher un compte…"
              className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
            />
          </div>

          {visibleStaff.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">
              Aucun compte. Passez un utilisateur en « Personnel » depuis Gestion des Utilisateurs.
            </p>
          ) : (
            <div className="divide-y">
              {visibleStaff.map((u) => (
                <div key={u.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-800">
                      {u.firstName} {u.lastName}
                    </div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </div>

                  {u.role === 'admin' ? (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-gray-800 text-white font-semibold">
                      Administrateur — tous les droits
                    </span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <select
                        value={u.staffRoleId || ''}
                        onChange={(e) => assignRole(u.id, e.target.value)}
                        className="border rounded-lg px-3 py-1.5 text-sm"
                      >
                        <option value="">— Aucun rôle (aucun accès) —</option>
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                        <FiBriefcase size={12} /> {u.merchantCount} marchand{u.merchantCount > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-3 flex items-start gap-1.5">
            <FiUsers size={13} className="mt-0.5 shrink-0" />
            Le portefeuille de marchands se règle dans <strong>Accès du personnel</strong> :
            il limite le rôle à certains marchands, il ne remplace pas les permissions.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminRolesPermissions;
